import React from "react";

import { Button } from "@common/components/Button";
import { Checkbox } from "@common/components/checkbox";
import { Flex } from "@common/components/Flex";
import Tree, { TreeSelectionType, flattenTree } from "@common/components/Tree";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "@common/components/modal";
import { Loading } from "@common/components/loading";
import { trDts } from "@modules/datasheets/utils/i18n";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { ImportFolder, ImportField } from "@modules/datasheets/services/import";

import "./import-datasheet-fields-picker.styles.scss";

type TreeNode = {
  id: string | number;
  name: React.ReactNode;
  children?: TreeNode[];
  isField?: boolean;
  imageURL?: string;
  imageURLSelected?: string;
};

type ImportDatasheetFieldsPickerProps = {
  show: boolean;
  file: File;
  folder: FolderPrimaryListDto;
  pedigreeFolders: ImportFolder[];
  individualFolders: ImportFolder[];
  pedigreeFields: Record<string, ImportField[]>;
  individualFields: Record<string, ImportField[]>;
  systemFields: Record<string, ImportField[]>;
  isImporting?: boolean;
  onCancel: () => void;
  onSubmit: (params: {
    folder: FolderPrimaryListDto;
    file: File;
    createNewFields: boolean;
    individualFieldIds: number[];
    pedigreeFieldIds: number[];
  }) => void;
};

/**
 * Lightweight React analogue of the GWT ImportDatasheetFormatFieldsPickerWnd.
 * Lets the user pick destination folder (already chosen) and optionally select
 * individual/pedigree fields plus a "create new fields" toggle.
 */
// Helper function to convert ImportFolder to TreeNode, including fields
const convertFolderToTreeNode = (
  folder: ImportFolder,
  context: "pedigree" | "individual",
  fieldsMap: Record<string, ImportField[]>
): TreeNode => {
  const children: TreeNode[] = [];

  // Add fields for this folder (fields are keyed by folder ID as string)
  const folderFields = fieldsMap[String(folder.id)] || [];
  folderFields.forEach((field) => {
    // Use prefixes that the selection extractor understands:
    //  - "f-ind-" for individual fields
    //  - "f-ped-" for pedigree fields
    const idPrefix = context === "individual" ? "f-ind-" : "f-ped-";
    children.push({
      id: `${idPrefix}${field.fieldid}`,
      name: field.fieldname,
      isField: true,
      imageURL: field.imageURL,
      imageURLSelected: field.imageURL,
    });
  });

  // Process child folders
  const displayChildren = folder.childrenForDisplay || folder.children || [];
  displayChildren.forEach((child) => {
    children.push(convertFolderToTreeNode(child, context, fieldsMap));
  });

  return {
    id: `folder-${context}-${folder.id}`,
    name: folder.name,
    children: children.length > 0 ? children : undefined,
    imageURL: folder.imageURL,
    imageURLSelected: folder.imageURLSelected,
  };
};

export const ImportDatasheetFieldsPicker: React.FC<ImportDatasheetFieldsPickerProps> = ({
  show,
  file,
  folder,
  pedigreeFolders,
  individualFolders,
  pedigreeFields,
  individualFields,
  isImporting,
  onCancel,
  onSubmit,
}) => {
  const [createNewFields, setCreateNewFields] = React.useState(false);
  const [treeItems, setTreeItems] = React.useState<TreeNode[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<Array<string | number>>([]);
  const [loading, setLoading] = React.useState(false);
  const selectAllCheckboxContainerRef = React.useRef<HTMLDivElement>(null);

  // Convert folders and fields to TreeNode format when component mounts or data changes
  React.useEffect(() => {
    const treeNodes: TreeNode[] = [];

    // Add Individual Data Folders root if there are individual folders or fields
    if (individualFolders.length > 0 || Object.keys(individualFields).length > 0) {
      const individualRoot: TreeNode = {
        id: "ind-root",
        name: "Individual Data Folders",
        imageURL: "gif/df_individual.gif",
        imageURLSelected: "gif/df_individual.gif",
        children: individualFolders.map((f) => convertFolderToTreeNode(f, "individual", individualFields)),
      };
      treeNodes.push(individualRoot);
    }

    // Add Pedigree Data Folders root if there are pedigree folders or fields
    if (pedigreeFolders.length > 0 || Object.keys(pedigreeFields).length > 0) {
      const pedigreeRoot: TreeNode = {
        id: "ped-root",
        name: "Pedigree Data Folders",
        imageURL: "gif/df_pedigree.gif",
        imageURLSelected: "gif/df_pedigree.gif",
        children: pedigreeFolders.map((f) => convertFolderToTreeNode(f, "pedigree", pedigreeFields)),
      };
      treeNodes.push(pedigreeRoot);
    }


    setTreeItems(treeNodes);
  }, [pedigreeFolders, individualFolders, pedigreeFields, individualFields]);

  // Get all field IDs from the tree (fields have IDs starting with "f-")
  const allFieldIds = React.useMemo(() => {
    const flattened = flattenTree(treeItems);
    return flattened
      .filter((item) => typeof item.id === "string" && item.id.toString().startsWith("f-"))
      .map((item) => item.id);
  }, [treeItems]);

  // Calculate select all state
  const selectedFieldIds = React.useMemo(() => {
    return selectedIds.filter((id) => typeof id === "string" && id.toString().startsWith("f-"));
  }, [selectedIds]);

  const allFieldsSelected = React.useMemo(() => {
    return allFieldIds.length > 0 && allFieldIds.every((id) => selectedFieldIds.includes(id));
  }, [allFieldIds, selectedFieldIds]);

  const someFieldsSelected = React.useMemo(() => {
    return selectedFieldIds.length > 0 && !allFieldsSelected;
  }, [selectedFieldIds, allFieldsSelected]);

  // Update indeterminate state of select all checkbox
  React.useEffect(() => {
    if (selectAllCheckboxContainerRef.current) {
      const input = selectAllCheckboxContainerRef.current.querySelector<HTMLInputElement>('input[type="checkbox"]');
      if (input) {
        input.indeterminate = someFieldsSelected;
      }
    }
  }, [someFieldsSelected]);

  const handleSelectAll = React.useCallback(
    (checked: boolean) => {
      if (checked) {
        // Select all fields and their parent folders
        // When using parentChild selection, we need to select the root folders to select all children
        const allIds = flattenTree(treeItems).map((item) => String(item.id));
        setSelectedIds(allIds);
      } else {
        // Deselect all
        setSelectedIds([]);
      }
    },
    [treeItems]
  );
  // Extract field IDs from selected items
  const { individualFieldIds, pedigreeFieldIds } = React.useMemo(() => {
    const ind: number[] = [];
    const ped: number[] = [];

    // Extract field IDs from selected IDs
    // Fields have IDs like "f-ind-{fieldid}", "f-ped-{fieldid}", or "f-system-{fieldid}"
    selectedIds.forEach((id) => {
      const idStr = String(id);
      if (idStr.startsWith("f-ind-")) {
        // Individual field: extract numeric ID
        const fieldId = parseInt(idStr.replace("f-ind-", ""), 10);
        if (!isNaN(fieldId)) {
          ind.push(fieldId);
        }
      } else if (idStr.startsWith("f-ped-")) {
        // Pedigree field: extract numeric ID
        const fieldId = parseInt(idStr.replace("f-ped-", ""), 10);
        if (!isNaN(fieldId)) {
          ped.push(fieldId);
        }
      }
    });

    return { individualFieldIds: ind, pedigreeFieldIds: ped };
  }, [selectedIds]);

  const handleSubmit = React.useCallback(() => {
    onSubmit({
      folder,
      file,
      createNewFields,
      individualFieldIds,
      pedigreeFieldIds,
    });
  }, [onSubmit, folder, file, createNewFields, individualFieldIds, pedigreeFieldIds]);
  return (
    <Modal
      show={show}
      backdrop="static"
      bsClass="bootstrap modal"
      className="DatasheetFormatManagement"
      dialogClassName="DatasheetFormatManagement__dialog"
      animation={false}
      onEscapeKeyDown={onCancel}
      onHide={onCancel}
    >
      <ModalHeader closeButton onHide={onCancel}>
        <ModalTitle>{trDts("Choose Fields to Import")}</ModalTitle>
      </ModalHeader>
      <ModalBody className="ImportDatasheetFieldsPicker__body">
        {isImporting && (
          <div className="ImportDatasheetFieldsPicker__overlay">
            <div className="ImportDatasheetFieldsPicker__overlay__content">
              <Loading size={60} />
              <div className="ImportDatasheetFieldsPicker__overlay__message">
                {trDts("importFormatOverlay")}
              </div>
            </div>
          </div>
        )}
        {!loading && (
          <div ref={selectAllCheckboxContainerRef} className="ImportDatasheetFieldsPicker__select-all">
            <Checkbox
              checked={allFieldsSelected}
              onChange={handleSelectAll}
              label={trDts("Select All")}
            />
          </div>
        )}
        <div
          className={`ImportDatasheetFieldsPicker__tree ${loading ? "ImportDatasheetFieldsPicker__tree--loading" : ""}`}
        >
          {loading ? (
            <div className="ImportDatasheetFieldsPicker__loading">{trDts("Loading")}...</div>
          ) : (
            <Tree
              items={treeItems}
              multiple
              selectionType={TreeSelectionType.parentChild}
              features={{ checkbox: true, collapsing: true, folderImg: true }}
              value={selectedIds}
              defaultCollapsed={true}
              preserveCollapsedState={false}
              normalizeId={String}
              onChange={setSelectedIds}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Flex justify="space-between" alignItems="center">
        <Flex>
          <Checkbox
            checked={createNewFields}
            onChange={() => setCreateNewFields(!createNewFields)}
            label={trDts("Allow import to create new fields")}
          />
        </Flex>
        <Flex justify="flex-end" gap="8px">
          <Button onClick={onCancel}>{trDts("Cancel")}</Button>
          <Button bsStyle="primary" onClick={handleSubmit}>
            {trDts("Import")}
          </Button>
        </Flex>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

